import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { keyframes } from "@emotion/react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-16px); }
`;

const PARTICLES: { left: string; top: string; size: number; delay: number }[] =
  [
    { left: "8%", top: "12%", size: 8, delay: 0 },
    { left: "18%", top: "72%", size: 5, delay: 0.4 },
    { left: "25%", top: "38%", size: 10, delay: 0.8 },
    { left: "72%", top: "15%", size: 6, delay: 1.2 },
    { left: "85%", top: "55%", size: 9, delay: 1.6 },
    { left: "92%", top: "30%", size: 4, delay: 2.0 },
    { left: "60%", top: "80%", size: 7, delay: 2.4 },
    { left: "45%", top: "90%", size: 5, delay: 2.8 },
  ];

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: `
          radial-gradient(circle at top right, #7956f7 0%, transparent 60%) no-repeat,
          radial-gradient(circle at bottom left, #7956f7 0%, transparent 60%) no-repeat,
          linear-gradient(145deg, #36276f, #4c3699 50%, #36276f)
        `,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {/* Floating particles — hidden on mobile */}
      {PARTICLES.map((p, i) => (
        <Box
          key={i}
          sx={{
            display: { xs: "none", sm: "block" },
            position: "absolute",
            left: p.left,
            top: p.top,
          }}
        >
          <motion.div
            animate={{ y: [0, -22, 0], opacity: [0.2, 0.55, 0.2] }}
            transition={{
              duration: 4 + i * 0.4,
              ease: "easeInOut",
              repeat: Infinity,
              delay: p.delay,
            }}
            style={{
              width: p.size,
              height: p.size,
              borderRadius: "50%",
              background: "rgba(146,239,143,0.6)",
              pointerEvents: "none",
            }}
          />
        </Box>
      ))}

      {/* Content column */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          px: 2,
          zIndex: 1,
          position: "relative",
        }}
      >
        {/* SVG robot */}
        <Box
          sx={{
            animation: `${fadeUp} 0.55s ease-out 0.1s both, ${floatY} 3.5s ease-in-out 0.7s infinite`,
          }}
        >
          <svg
            width="180"
            height="180"
            viewBox="0 0 180 180"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="22" cy="28" r="2.5" fill="rgba(255,255,255,0.45)" />
            <circle cx="158" cy="44" r="1.8" fill="rgba(255,255,255,0.35)" />
            <circle cx="145" cy="20" r="2.2" fill="rgba(255,255,255,0.40)" />
            <circle cx="38" cy="148" r="1.6" fill="rgba(255,255,255,0.30)" />

            <rect
              x="20"
              y="30"
              width="130"
              height="100"
              rx="22"
              fill="url(#bubbleGrad)"
            />
            <path d="M40 130 L28 152 L62 130 Z" fill="#5c3fbf" />

            <path
              d="M152 55 Q165 70 152 85"
              stroke="#92EF8F"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
              opacity="0.7"
            />
            <path
              d="M157 48 Q175 70 157 92"
              stroke="#92EF8F"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
              opacity="0.4"
            />

            <circle cx="70" cy="74" r="13" fill="white" />
            <circle cx="70" cy="74" r="8" fill="#92EF8F" />
            <circle cx="70" cy="74" r="4" fill="#2a1a5e" />
            <circle cx="67" cy="71" r="2" fill="white" opacity="0.8" />

            <circle cx="110" cy="74" r="13" fill="white" />
            <circle cx="110" cy="74" r="8" fill="#92EF8F" />
            <circle cx="110" cy="74" r="4" fill="#2a1a5e" />
            <circle cx="107" cy="71" r="2" fill="white" opacity="0.8" />

            <path
              d="M72 102 Q80 97 90 102 Q100 107 108 102"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
            />

            <text
              x="126"
              y="60"
              fontSize="22"
              fontWeight="bold"
              fill="#92EF8F"
              fontFamily="Poppins, sans-serif"
            >
              ?
            </text>

            <line
              x1="85"
              y1="30"
              x2="80"
              y2="8"
              stroke="#92EF8F"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <circle cx="80" cy="6" r="5" fill="#92EF8F">
              <animate
                attributeName="opacity"
                values="0.4;1;0.4"
                dur="2.5s"
                repeatCount="indefinite"
              />
            </circle>

            <defs>
              <linearGradient
                id="bubbleGrad"
                x1="20"
                y1="30"
                x2="150"
                y2="130"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="#7956f7" />
                <stop offset="100%" stopColor="#4c3699" />
              </linearGradient>
            </defs>
          </svg>
        </Box>

        {/* 404 */}
        <Box sx={{ animation: `${fadeUp} 0.55s ease-out 0.25s both` }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "6rem", sm: "9rem", md: "11rem" },
              fontWeight: 900,
              lineHeight: 1,
              mt: 1,
              background: "linear-gradient(135deg, #ffffff 30%, #92EF8F 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {t("notFound.title")}
          </Typography>
        </Box>

        {/* Headline */}
        <Box sx={{ animation: `${fadeUp} 0.55s ease-out 0.4s both` }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "white",
              mt: 1,
              mb: 2,
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {t("notFound.headline")}
          </Typography>
        </Box>

        {/* Subtitle */}
        <Box sx={{ animation: `${fadeUp} 0.55s ease-out 0.55s both` }}>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.8)",
              maxWidth: 480,
              mx: "auto",
              mb: 1,
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {t("notFound.subtitle")}
          </Typography>
        </Box>

        {/* Hint */}
        <Box sx={{ animation: `${fadeUp} 0.55s ease-out 0.7s both` }}>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.45)",
              display: "block",
              mb: 4,
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {t("notFound.hint")}
          </Typography>
        </Box>

        {/* Button */}
        <Box sx={{ animation: `${fadeUp} 0.55s ease-out 0.85s both` }}>
          <motion.div whileHover={{ scale: 1.07 }} whileTap={{ scale: 0.96 }}>
            <Button
              variant="contained"
              onClick={() => navigate("/")}
              sx={{
                backgroundColor: "#92EF8F",
                color: "#36276F",
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1rem",
                px: 4,
                py: 1.5,
                fontFamily: "'Poppins', sans-serif",
                "&:hover": { backgroundColor: "#7de07a" },
              }}
            >
              {t("notFound.backButton")}
            </Button>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

export default NotFoundPage;
